import { z } from 'zod';
// Brand inputs — spec §0a.9. Founder→Mag context that doesn't fit a chat
// message. Backs the "Brief Mag" composer on the home page.
//
// The body cap is generous (4kB) so a pasted email or a multi-paragraph
// event brief fits. The agent that consumes the input handles its own
// truncation downstream — keeping the raw payload lets future agents
// re-read with different summarization.
export const brandInputKindSchema = z.enum(['event', 'asset_drop', 'pinned_brief', 'aside']);
export const brandInputStatusSchema = z.enum(['open', 'acknowledged', 'consumed', 'expired']);
/** Channel mag's receipt landed on. Mirrors the notification channel
 *  union but kept local so a deps shake-up doesn't ripple. */
export const brandInputAcknowledgedViaSchema = z.enum([
    'in_app',
    'telegram',
    'slack',
    'email',
    'web_push',
]);
/** Downstream agent that consumed the input. Constrained at the
 *  application layer so the router can extend safely without a schema
 *  bump every time a new sink lands. */
export const brandInputConsumedByKindSchema = z.enum([
    'meta_content_plan_revise',
    'asset_tagger',
    'paid_drafter_pinned',
    'episodic_only',
]);
/** Founder-facing payload — what the composer POSTs. The discriminating
 *  field is `kind`; per-kind fields are constrained accordingly. */
export const newBrandInputSchema = z
    .object({
    kind: brandInputKindSchema,
    body: z.string().trim().min(1).max(4000),
    eventDate: z.string().date().optional(),
    assetIds: z.array(z.string().uuid()).optional(),
})
    .superRefine((val, ctx) => {
    if (val.kind === 'event' && !val.eventDate) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['eventDate'],
            message: 'eventDate is required when kind=event',
        });
    }
    if (val.kind === 'asset_drop' && (!val.assetIds || val.assetIds.length === 0)) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['assetIds'],
            message: 'assetIds must include at least one asset when kind=asset_drop',
        });
    }
    // event_date / asset_ids must not be set for the wrong kind — keeps
    // the row's discriminant + payload coherent. The /home composer
    // clears the fields when the founder switches kinds.
    if (val.kind !== 'event' && val.eventDate) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['eventDate'],
            message: 'eventDate may only be set when kind=event',
        });
    }
    if (val.kind !== 'asset_drop' && val.assetIds && val.assetIds.length > 0) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['assetIds'],
            message: 'assetIds may only be set when kind=asset_drop',
        });
    }
});
/** Server-side response shape. ISO datetime strings on the wire. */
export const brandInputSchema = z.object({
    id: z.string().uuid(),
    brandId: z.string().uuid(),
    organizationId: z.string().uuid(),
    userId: z.string().uuid().nullable(),
    kind: brandInputKindSchema,
    body: z.string(),
    eventDate: z.string().date().nullable(),
    assetIds: z.array(z.string().uuid()).nullable(),
    expiresAt: z.string().datetime().nullable(),
    status: brandInputStatusSchema,
    acknowledgedAt: z.string().datetime().nullable(),
    acknowledgedVia: brandInputAcknowledgedViaSchema.nullable(),
    consumedByKind: brandInputConsumedByKindSchema.nullable(),
    consumedAt: z.string().datetime().nullable(),
    createdAt: z.string().datetime(),
});
export const brandInputListResponseSchema = z.object({
    inputs: z.array(brandInputSchema),
});
//# sourceMappingURL=brand-input.js.map